<template>
  <div class="stock-data-browser">
    <!-- 顶部标题栏 -->
    <div class="header">
      <div class="header-left">
        <span class="logo">布布出品</span>
        <span class="separator">·</span>
        <span class="title">熊熊数据浏览器</span>
        <span v-if="classInfo" class="class-filter-info">
          <el-tag type="info" size="small" closable @close="handleClearFilter">
            {{ classInfo.className }}
          </el-tag>
        </span>
      </div>
      <div class="header-right">
        <el-button v-if="classInfo" type="primary" size="small" @click="handleBack">
          <el-icon><ArrowLeft /></el-icon> 返回班级列表
        </el-button>
        <el-button link><el-icon><Service /></el-icon> 在线客服</el-button>
        <el-button link><el-icon><Setting /></el-icon></el-button>
      </div>
    </div>

    <!-- 主内容区 -->
    <div class="main-content">
      <!-- 左侧指标面板 -->
      <div class="left-panel">
        <div class="panel-tabs">
          <div 
            class="tab-item" 
            :class="{ active: currentLeftTab === 0 }"
            @click="currentLeftTab = 0"
          >
            添加指标
          </div>
          <div 
            class="tab-item" 
            :class="{ active: currentLeftTab === 1 }"
            @click="currentLeftTab = 1"
          >
            我的指标
          </div>
        </div>
        
        <div class="panel-content">
          <!-- 添加指标标签页 -->
          <div v-show="currentLeftTab === 0">
            <IndicatorSearch 
              v-if="indicatorTreeData"
              :indicator-tree-data="indicatorTreeData" 
              @select="handleSearchSelect" 
            />
            
            <div class="tree-container" v-loading="treeLoading">
              <el-tree
                v-if="indicatorTreeData && indicatorTreeData.length > 0"
                :data="indicatorTreeData"
                :props="defaultProps"
                @node-click="handleNodeClick"
                :expand-on-click-node="false"
                highlight-current
              >
                <template #default="{ node, data }">
                  <span class="tree-node" :class="{ 'is-leaf': !data.children }">
                    <el-icon v-if="data.children && data.children.length">
                      <FolderOpened v-if="node.expanded" />
                      <Folder v-else />
                    </el-icon>
                    <el-icon v-else><Document /></el-icon>
                    <span class="node-label" :title="node.label">{{ node.label }}</span>
                  </span>
                </template>
              </el-tree>
              <div v-else class="empty-tree">
                暂无指标数据
              </div>
            </div>
          </div>
          
          <!-- 我的指标标签页 -->
          <div v-show="currentLeftTab === 1">
            <div class="favorites-container" v-loading="favoritesLoading">
              <div 
                v-for="item in favoriteIndicators" 
                :key="item.id"
                class="favorite-item"
                @click="handleFavoriteClick(item)"
              >
                <el-icon><StarFilled /></el-icon>
                <span class="item-label" :title="item.label">{{ item.label }}</span>
              </div>
              <div v-if="favoriteIndicators.length === 0" class="empty-favorites">
                暂无收藏的指标
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧内容区 -->
      <div class="right-content">
        <!-- 顶部工具栏 -->
        <div class="toolbar">
          <el-button type="primary" :icon="Download" size="small">提取数据</el-button>
          <el-button :icon="Filter" size="small" @click="filterDialogVisible = true">指标筛选</el-button>
          <el-button :icon="Management" size="small" @click="openIndicatorManage">指标管理</el-button>
          <el-button :icon="User" size="small" @click="openStudentManage">学生管理</el-button>
          <el-dropdown @command="handleExport">
            <el-button :icon="Document" size="small">导出Excel <el-icon class="el-icon--right"><ArrowDown /></el-icon></el-button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="selected">导出勾选</el-dropdown-item>
                <el-dropdown-item command="current">导出当前页</el-dropdown-item>
                <el-dropdown-item command="all">导出全部</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>

        <!-- 数据表格区域 -->
        <div class="table-area">
          <el-table
            :data="tableData"
            border
            stripe
            style="width: 100%"
            height="calc(100vh - 200px)"
            @selection-change="handleSelectionChange"
            highlight-current-row
            :key="tableKey"
          >
            <!-- 固定列 -->
            <el-table-column type="selection" width="40" fixed="left" />
            <el-table-column type="index" label="序号" width="60" fixed="left" align="center" />
            <el-table-column prop="studentNo" label="学号" width="150" fixed="left" sortable />
            <el-table-column prop="name" label="姓名" width="100" fixed="left" sortable />
            
            <!-- 动态列 - 根据选中的指标显示 -->
            <el-table-column 
              v-for="(col, index) in selectedColumns" 
              :key="col.prop"
              :prop="col.prop" 
              :width="col.width || 120"
              :sortable="col.sortable !== false"
              :align="col.align || 'left'"
              :show-overflow-tooltip="true"
            >
              <template #header>
                <div class="column-header">
                  <span class="column-title">{{ col.label }}</span>
                  <div class="column-icons">
                    <el-icon 
                      class="column-star-icon" 
                      :class="{ 'is-favorite': isFavorite(col) }"
                      @click.stop="toggleFavorite(col)"
                    >
                      <StarFilled v-if="isFavorite(col)" />
                      <Star v-else />
                    </el-icon>
                    <el-icon class="column-delete-icon" @click.stop="removeColumn(index)"><Close /></el-icon>
                  </div>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 底部区域 -->
        <div class="bottom-area">
          <div class="tabs-area">
            <div class="tab-list">
              <div 
                v-for="(tab, index) in tabList" 
                :key="index"
                class="tab-item"
                :class="{ active: currentTab === index }"
                @click="currentTab = index"
              >
                <span>{{ tab.name }}</span>
                <el-icon class="tab-close" @click.stop="closeTab(index)"><Close /></el-icon>
              </div>
            </div>
            <div class="tab-actions">
              <el-button link size="small"><el-icon><Edit /></el-icon></el-button>
              <el-button link size="small"><el-icon><Close /></el-icon></el-button>
              <el-button link size="small"><el-icon><Plus /></el-icon></el-button>
            </div>
          </div>
          
          <div class="pagination-area">
            <span class="summary">
              总计学生数: {{ totalCount }}，筛选学生数: {{ filteredCount }}，已勾选: {{ selectedCount }}
            </span>
            <div class="pagination-wrapper">
              <el-pagination
                v-model:current-page="currentPage"
                v-model:page-size="pageSize"
                :page-sizes="[50, 100, 200]"
                :total="filteredCount"
                layout="total, prev, pager, next, sizes, jumper"
                background
                small
              />
              <span class="page-size-label">条/页</span>
              <span class="goto-label">跳至</span>
              <el-input v-model="gotoPage" size="small" style="width: 50px" />
              <span>页</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- 筛选弹窗 -->
  <FilterDialog
    v-model:visible="filterDialogVisible"
    :columns="filterColumns"
    @apply="handleFilterApply"
  />

  <!-- 指标管理弹窗 -->
  <el-dialog v-model="indicatorManageDialogVisible" title="指标管理" width="700px">
    <div class="indicator-manage-header">
      <el-button type="primary" size="small" @click="addIndicatorVisible = true">新增课程指标</el-button>
    </div>
    <el-table :data="courseIndicatorList" border stripe style="width: 100%; margin-top: 10px">
      <el-table-column label="课程名称" prop="label" width="450">
        <template #default="{ row }">
          <el-input v-if="editingIndicatorId === row.id" v-model="editingIndicatorName" size="small" placeholder="请输入课程名称" />
          <span v-else>{{ row.label }}</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" align="center">
        <template #default="{ row }">
          <el-button v-if="editingIndicatorId === row.id" type="success" size="small" @click="handleSaveEdit(row)">完成</el-button>
          <el-button v-else type="primary" size="small" @click="handleEdit(row)">修改</el-button>
          <el-button type="danger" size="small" @click="handleDelete(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>
    
    <!-- 新增指标弹窗 -->
    <el-dialog v-model="addIndicatorVisible" title="新增课程指标" width="500px">
      <el-form label-width="100px" :model="addIndicatorForm">
        <el-form-item label="课程代码" prop="code">
          <el-input v-model="addIndicatorForm.code" placeholder="例如：003" />
          <div style="font-size: 12px; color: #999; margin-top: 5px">
            完整ID将自动生成为：{{ majorCode }}_{{ academicYearSuffix }}.{{ addIndicatorForm.code || 'XXX' }}
          </div>
        </el-form-item>
        <el-form-item label="课程名称" prop="name">
          <el-input v-model="addIndicatorForm.name" placeholder="请输入课程名称" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="addIndicatorVisible = false">取消</el-button>
        <el-button type="primary" @click="handleAddIndicator">确定</el-button>
      </template>
    </el-dialog>
  </el-dialog>

  <!-- 学生管理弹窗 -->
  <el-dialog v-model="studentManageDialogVisible" title="学生管理" width="900px">
    <div class="student-manage-header">
      <el-input v-model="studentSearchKeyword" placeholder="搜索学号/姓名/身份证号" size="small" style="width: 300px; margin-right: 10px" @input="loadStudentList" />
      <el-button type="primary" size="small" @click="openAddStudent">新增学生</el-button>
    </div>
    <el-table :data="studentList" border stripe style="width: 100%; margin-top: 10px" v-loading="studentLoading">
      <el-table-column label="学号" prop="studentNo" width="120" />
      <el-table-column label="姓名" prop="name" width="100" />
      <el-table-column label="年龄" prop="age" width="80" align="center" />
      <el-table-column label="所属专业" prop="major" width="180" />
      <el-table-column label="导员" prop="counselorInfo" width="100" />
      <el-table-column label="联系电话" prop="phone" width="130" />
      <el-table-column label="父母姓名" prop="parentName" width="100" />
      <el-table-column label="父母电话" prop="parentPhone" width="130" />
      <el-table-column label="操作" align="center" width="120">
        <template #default="{ row }">
          <el-button type="primary" size="small" @click="openEditStudent(row)">修改</el-button>
          <el-button type="danger" size="small" @click="handleDeleteStudent(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>
    <div class="pagination-area" style="margin-top: 10px">
      <span>总计 {{ studentTotal }} 人</span>
      <el-pagination
        v-model:current-page="studentPageNum"
        v-model:page-size="studentPageSize"
        :page-sizes="[10, 20, 50]"
        :total="studentTotal"
        layout="total, prev, pager, next, sizes, jumper"
        background
        small
        @current-change="loadStudentList"
        @size-change="loadStudentList"
      />
    </div>

    <!-- 新增/编辑学生弹窗 -->
    <el-dialog v-model="studentEditVisible" :title="isEditStudent ? '编辑学生' : '新增学生'" width="750px">
      <el-form :model="studentForm" label-width="100px" :rules="studentRules" ref="studentFormRef">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="学号" prop="studentNo">
              <el-input v-model="studentForm.studentNo" :disabled="isEditStudent" placeholder="请输入8位学号" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="姓名" prop="name">
              <el-input v-model="studentForm.name" placeholder="请输入姓名" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="年龄" prop="age">
              <el-input-number v-model="studentForm.age" :min="16" :max="30" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="身份证号" prop="idCard">
              <el-input v-model="studentForm.idCard" placeholder="请输入18位身份证号" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="所属学院" prop="collegeCode">
              <el-select v-model="studentForm.collegeCode" placeholder="请选择学院" @change="onCollegeChange">
                <el-option v-for="item in collegeList" :key="item.collegeCode" :label="item.collegeName" :value="item.collegeCode" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="所属专业" prop="majorCode">
              <el-select v-model="studentForm.majorCode" placeholder="请选择专业" @change="onMajorChange">
                <el-option v-for="item in majorList" :key="item.majorCode" :label="item.majorName" :value="item.majorCode" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="所属班级" prop="classCode">
              <el-select v-model="studentForm.classCode" placeholder="请选择班级">
                <el-option v-for="item in classList" :key="item.classCode" :label="item.className" :value="item.classCode" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="导员" prop="counselorInfo">
              <el-select v-model="studentForm.counselorInfo" placeholder="请选择导员" @change="onCounselorChange">
                <el-option v-for="item in counselorList" :key="item.counselorId" :label="item.counselorName" :value="item.counselorName" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="本人电话" prop="phone">
              <el-input v-model="studentForm.phone" placeholder="请输入11位手机号" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="导员电话" prop="counselorPhone">
              <el-input v-model="studentForm.counselorPhone" placeholder="导员电话" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="父母姓名" prop="parentName">
              <el-input v-model="studentForm.parentName" placeholder="请输入父母姓名" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="父母电话" prop="parentPhone">
              <el-input v-model="studentForm.parentPhone" placeholder="请输入父母联系电话" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="籍贯">
              <el-cascader v-model="studentForm.area" :options="areaOptions" placeholder="请选择省/市/区" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="详细地址" prop="street">
              <el-input v-model="studentForm.street" placeholder="请输入详细地址" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <template #footer>
        <el-button @click="studentEditVisible = false">取消</el-button>
        <el-button type="primary" @click="saveStudent">保存</el-button>
      </template>
    </el-dialog>
  </el-dialog>
</template>

<script>
/* eslint-disable */
import { ref, reactive, onMounted, watch, computed } from 'vue'
import { useRouter } from 'vue-router'
import request from '@/utils/request'
import { ElMessage, ElMessageBox } from 'element-plus'
import FilterDialog from './FilterDialog.vue'
import IndicatorSearch from './IndicatorSearch.vue'
import {
  ArrowDown,
  ArrowLeft,
  Service,
  Setting,
  Folder,
  FolderOpened,
  Document,
  Download,
  Filter,
  CircleClose,
  Management,
  Close,
  Edit,
  Plus,
  Star,
  StarFilled,
  User
} from '@element-plus/icons-vue'

export default {
  name: 'StockDataBrowser',
  props: {
    classInfo: {
      type: Object,
      default: null
    }
  },
  components: {
    // 在 template 中作为组件标签使用的图标
    ArrowDown,
    ArrowLeft,
    Service,
    Setting,
    Folder,
    FolderOpened,
    Document,
    Close,
    Edit,
    Plus,
    Star,
    StarFilled,
    User,
    FilterDialog,
    IndicatorSearch
  },
  setup(props) {
    const router = useRouter()
    const currentLeftTab = ref(0)
    const selectedCount = ref(0)
    const currentPage = ref(1)
    const pageSize = ref(50)
    const totalCount = ref(5484)
    const filteredCount = ref(5484)
    const gotoPage = ref(1)
    const currentTab = ref(0)
    const tableKey = ref(0) // 用于强制表格重新渲染

    const defaultProps = {
      children: 'children',
      label: 'label',
    }

    // 已选中的列
    const selectedColumns = reactive([])

    // 下划线转驼峰
    const toCamelCase = (str) => {
      if (!str) return ''
      return str.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase())
    }

    // 根据mapper智能生成列配置
    const generateColumnConfig = (mapper, label, indexType) => {
      // 默认配置
      let config = {
        prop: '',  // 将在下面生成
        label: label || '',
        width: 120,
        sortable: false,
        align: 'left',
        indexType: indexType || 'STRING'  // 添加indexType，默认STRING
      }
      
      // 如果mapper为空，直接返回默认配置
      if (!mapper) {
        return config
      }

      // 判断是否为专业课指标（格式：10001001_18.001）
      const isCourseMapper = /^\d{8}_\d{2}\.\d{3}$/.test(mapper)
      if (isCourseMapper) {
        // 专业课指标：转换为数据库字段格式
        // 输入: 10001001_18.001 → 输出: score1000100118001
        config.prop = 'score' + mapper.replace(/[_]/g, '').replace(/\./g, '')
      } else {
        // 普通指标：使用驼峰格式
        config.prop = toCamelCase(mapper)
      }

      // 根据字段名特征智能判断
      // 1. 金额类字段（包含amount, value, capital, shares）- 右对齐
      if (/amount|value|capital|shares|price|money|fee|fine/i.test(mapper)) {
        config.align = 'right'
        config.width = 140
      }

      // 2. 日期类字段 - 固定宽度
      if (/date|time/i.test(mapper)) {
        config.width = 150
      }

      // 3. 长文本类字段 - 加宽
      if (/intro|scope|business|title|detail/i.test(mapper)) {
        config.width = 200
      }

      // 4. 名称类字段 - 适中宽度
      if (/name|representative/i.test(mapper)) {
        config.width = 140
      }

      // 5. 是否类字段 - 固定宽度
      if (/is_/i.test(mapper)) {
        config.width = 140
      }

      return config
    }

    // 指标树形数据（从后端加载）
    const indicatorTreeData = reactive([])
    const treeLoading = ref(false)
    
    // 收藏指标数据
    const favoriteIndicators = reactive([])
    const favoritesLoading = ref(false)

    // 表格数据
    const tableData = reactive([])
    const loading = ref(false)

    // 标签页数据
    const tabList = reactive([
      { name: '数据浏览器1' },
    ])

    // 筛选弹窗显示状态
    const filterDialogVisible = ref(false)

    // 当前筛选条件
    const currentFilters = ref([])

    // 指标管理弹窗
    const indicatorManageDialogVisible = ref(false)
    const addIndicatorVisible = ref(false)
    const courseIndicatorList = reactive([])
    const editingIndicatorId = ref(null)
    const editingIndicatorName = ref('')
    const addIndicatorForm = reactive({
      code: '',
      name: ''
    })

    // 计算专业代码和学年后缀
    const majorCode = computed(() => props.classInfo?.majorCode || '')
    const academicYearSuffix = computed(() => {
      const year = props.classInfo?.academicYear
      return year ? year.toString().slice(-2) : ''
    })

    // 加载课程指标列表
    const loadCourseIndicators = async () => {
      courseIndicatorList.splice(0, courseIndicatorList.length)
      try {
        const res = await request.post('/browser-config/course-indicators', {
          majorCode: props.classInfo.majorCode,
          academicYear: props.classInfo.academicYear
        })
        if (res.data) {
          courseIndicatorList.push(...res.data)
        }
      } catch (e) {
        console.error('加载课程指标失败', e)
        ElMessage.error('加载课程指标失败')
      }
    }

    // 打开指标管理弹窗
    const openIndicatorManage = () => {
      if (!props.classInfo) {
        ElMessage.warning('请先选择班级')
        return
      }
      loadCourseIndicators()
      indicatorManageDialogVisible.value = true
    }

    // 编辑指标
    const handleEdit = (row) => {
      editingIndicatorId.value = row.id
      editingIndicatorName.value = row.label
    }

    // 保存编辑
    const handleSaveEdit = async (row) => {
      if (!editingIndicatorName.value.trim()) {
        ElMessage.warning('课程名称不能为空')
        return
      }
      try {
        // 调用修改接口
        const res = await request.post('/browser-config/update', {
          id: row.id,
          label: editingIndicatorName.value.trim()
        })
        if (res.data.code === 200) {
          ElMessage.success('修改成功')
          row.label = editingIndicatorName.value.trim()
          editingIndicatorId.value = null
          // 刷新指标树
          fetchTreeData()
        } else {
          ElMessage.error(res.data.msg || '修改失败')
        }
      } catch (e) {
        console.error('修改失败', e)
        ElMessage.error('修改失败')
      }
    }

    // 删除指标
    const handleDelete = async (row) => {
      try {
        await ElMessageBox.confirm(`确定要删除课程【${row.label}】吗？删除后无法恢复`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        const res = await request.post(`/browser-config/delete/${row.id}`)
        if (res.data.code === 200) {
          ElMessage.success('删除成功')
          loadCourseIndicators()
          // 刷新指标树
          fetchTreeData()
          // 如果已添加到表格列，删除对应的列
          const index = selectedColumns.findIndex(col => col.id === row.id)
          if (index > -1) {
            selectedColumns.splice(index, 1)
            tableKey.value++
          }
        } else {
          ElMessage.error(res.data.msg || '删除失败')
        }
      } catch (e) {
        // 取消删除
      }
    }

    // 新增指标
    const handleAddIndicator = async () => {
      if (!addIndicatorForm.code.trim() || !addIndicatorForm.name.trim()) {
        ElMessage.warning('课程代码和名称不能为空')
        return
      }
      if (!/^\d{3}$/.test(addIndicatorForm.code.trim())) {
        ElMessage.warning('课程代码必须是3位数字')
        return
      }
      const id = `${majorCode.value}_${academicYearSuffix.value}.${addIndicatorForm.code.trim()}`
      try {
        const res = await request.post('/browser-config/add', {
          id,
          label: addIndicatorForm.name.trim(),
          mapper: id,
          parentId: majorCode.value,
          indexType: 'NUMBER'
        })
        if (res.data.code === 200) {
          ElMessage.success('新增成功')
          addIndicatorVisible.value = false
          addIndicatorForm.code = ''
          addIndicatorForm.name = ''
          loadCourseIndicators()
          // 刷新指标树
          fetchTreeData()
        } else {
          ElMessage.error(res.data.msg || '新增失败')
        }
      } catch (e) {
        console.error('新增失败', e)
        ElMessage.error('新增失败')
      }
    }

    // ==================== 学生管理相关 ====================
    const studentManageDialogVisible = ref(false)
    const studentEditVisible = ref(false)
    const isEditStudent = ref(false)
    const studentSearchKeyword = ref('')
    const studentList = reactive([])
    const studentLoading = ref(false)
    const studentPageNum = ref(1)
    const studentPageSize = ref(20)
    const studentTotal = ref(0)
    const studentFormRef = ref(null)
    const studentForm = reactive({
      id: null,
      studentNo: '',
      name: '',
      age: 18,
      idCard: '',
      collegeCode: '',
      majorCode: '',
      classCode: '',
      counselorInfo: '',
      counselorPhone: '',
      phone: '',
      parentName: '',
      parentPhone: '',
      area: [],
      province: '',
      city: '',
      district: '',
      street: ''
    })
    const studentRules = {
      studentNo: [
        { required: true, message: '请输入学号', trigger: 'blur' },
        { pattern: /^\d{8}$/, message: '学号必须是8位数字', trigger: 'blur' }
      ],
      name: [
        { required: true, message: '请输入姓名', trigger: 'blur' }
      ],
      collegeCode: [
        { required: true, message: '请选择学院', trigger: 'change' }
      ],
      majorCode: [
        { required: true, message: '请选择专业', trigger: 'change' }
      ],
      classCode: [
        { required: true, message: '请选择班级', trigger: 'change' }
      ],
      counselorInfo: [
        { required: true, message: '请选择导员', trigger: 'change' }
      ]
    }
    const collegeList = reactive([])
    const majorList = reactive([])
    const classList = reactive([])
    const counselorList = reactive([])
    const areaOptions = ref([]) // 省市区数据，实际项目可引入element-plus的地区数据

    // 打开学生管理弹窗
    const openStudentManage = () => {
      if (!props.classInfo) {
        ElMessage.warning('请先选择班级')
        return
      }
      studentPageNum.value = 1
      studentSearchKeyword.value = ''
      loadStudentList()
      loadCollegeList()
      studentManageDialogVisible.value = true
    }

    // 加载学生列表
    const loadStudentList = async () => {
      studentLoading.value = true
      try {
        const res = await request.post('/student/manage/list', {
          classCode: props.classInfo.classCode,
          keyword: studentSearchKeyword.value,
          pageNum: studentPageNum.value,
          pageSize: studentPageSize.value
        })
        studentList.splice(0, studentList.length)
        if (res.data.list) {
          studentList.push(...res.data.list)
          studentTotal.value = res.data.total || 0
        }
      } catch (e) {
        console.error('加载学生列表失败', e)
        ElMessage.error('加载学生列表失败')
      } finally {
        studentLoading.value = false
      }
    }

    // 加载学院列表
    const loadCollegeList = async () => {
      try {
        const res = await request.get('/common/colleges')
        collegeList.splice(0, collegeList.length)
        collegeList.push(...res.data)
      } catch (e) {
        console.error('加载学院列表失败', e)
      }
    }

    // 学院变化，加载对应专业
    const onCollegeChange = async (collegeCode) => {
      studentForm.majorCode = ''
      studentForm.classCode = ''
      studentForm.counselorInfo = ''
      majorList.splice(0, majorList.length)
      classList.splice(0, classList.length)
      counselorList.splice(0, counselorList.length)
      try {
        const res = await request.get(`/common/majors/${collegeCode}`)
        majorList.push(...res.data)
      } catch (e) {
        console.error('加载专业列表失败', e)
      }
    }

    // 专业变化，加载对应班级和导员
    const onMajorChange = async (majorCode) => {
      studentForm.classCode = ''
      studentForm.counselorInfo = ''
      classList.splice(0, classList.length)
      counselorList.splice(0, counselorList.length)
      try {
        const [classRes, counselorRes] = await Promise.all([
          request.get(`/common/classes/${majorCode}`),
          request.get(`/common/counselors/${majorCode}`)
        ])
        classList.push(...classRes.data)
        counselorList.push(...counselorRes.data)
        // 默认填充当前班级
        if (props.classInfo && classList.some(item => item.classCode === props.classInfo.classCode)) {
          studentForm.classCode = props.classInfo.classCode
        }
      } catch (e) {
        console.error('加载班级/导员列表失败', e)
      }
    }

    // 导员变化，自动填充导员电话
    const onCounselorChange = (counselorName) => {
      const counselor = counselorList.find(item => item.counselorName === counselorName)
      if (counselor) {
        studentForm.counselorPhone = counselor.counselorPhone || ''
      }
    }

    // 打开新增学生弹窗
    const openAddStudent = () => {
      isEditStudent.value = false
      resetStudentForm()
      // 默认填充当前班级的学院、专业、导员
      if (props.classInfo) {
        studentForm.collegeCode = props.classInfo.collegeCode
        onCollegeChange(props.classInfo.collegeCode).then(() => {
          studentForm.majorCode = props.classInfo.majorCode
          onMajorChange(props.classInfo.majorCode).then(() => {
            studentForm.classCode = props.classInfo.classCode
          })
        })
      }
      studentEditVisible.value = true
    }

    // 打开编辑学生弹窗
    const openEditStudent = (row) => {
      isEditStudent.value = true
      resetStudentForm()
      Object.assign(studentForm, row)
      // 回显省市区
      if (row.province && row.city && row.district) {
        studentForm.area = [row.province, row.city, row.district]
      }
      // 加载对应学院、专业、导员数据
      loadCollegeList().then(() => {
        studentForm.collegeCode = row.collegeCode
        onCollegeChange(row.collegeCode).then(() => {
          studentForm.majorCode = row.majorCode
          onMajorChange(row.majorCode)
        })
      })
      studentEditVisible.value = true
    }

    // 重置表单
    const resetStudentForm = () => {
      Object.assign(studentForm, {
        id: null,
        studentNo: '',
        name: '',
        age: 18,
        idCard: '',
        collegeCode: '',
        majorCode: '',
        classCode: '',
        counselorInfo: '',
        counselorPhone: '',
        phone: '',
        parentName: '',
        parentPhone: '',
        area: [],
        province: '',
        city: '',
        district: '',
        street: ''
      })
      if (studentFormRef.value) {
        studentFormRef.value.resetFields()
      }
    }

    // 保存学生
    const saveStudent = async () => {
      if (!studentFormRef.value) return
      await studentFormRef.value.validate()
      try {
        // 处理省市区
        if (studentForm.area && studentForm.area.length === 3) {
          studentForm.province = studentForm.area[0]
          studentForm.city = studentForm.area[1]
          studentForm.district = studentForm.area[2]
        }
        const url = isEditStudent.value ? '/student/manage/update' : '/student/manage/add'
        const res = await request.post(url, studentForm)
        if (res.data.code === 200) {
          ElMessage.success(isEditStudent.value ? '修改成功' : '新增成功')
          studentEditVisible.value = false
          loadStudentList()
          // 刷新表格数据
          fetchData()
        } else {
          ElMessage.error(res.data.msg || '操作失败')
        }
      } catch (e) {
        console.error('保存学生失败', e)
        ElMessage.error('保存学生失败')
      }
    }

    // 删除学生
    const handleDeleteStudent = async (row) => {
      try {
        await ElMessageBox.confirm(`确定要删除学生【${row.name}】吗？删除后该学生数据将从班级移除`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        const res = await request.post(`/student/manage/delete/${row.id}`)
        if (res.data.code === 200) {
          ElMessage.success('删除成功')
          loadStudentList()
          // 刷新表格数据
          fetchData()
        } else {
          ElMessage.error(res.data.msg || '删除失败')
        }
      } catch (e) {
        // 取消删除
      }
    }

    // 处理搜索结果选择
    const handleSearchSelect = (item) => {
      // 添加null检查
      if (!item) return;
      
      // 使用现有的 handleNodeClick 方法处理
      if (typeof handleNodeClick === 'function') {
        handleNodeClick(item);
      }
    };

    // 处理指标树节点点击
    const handleNodeClick = (data) => {
      // 添加null检查
      if (!data) return;
      
      // 只有叶子节点（具体指标）才添加列
      if (!data.children || data.children.length === 0) {
        // 使用mapper、label和indexType动态生成列配置
        const config = generateColumnConfig(data.mapper, data.label, data.indexType)
        // 保存指标ID和收藏状态
        config.id = data.id
        config.favorites = data.favorites || 0
        // 检查是否已存在该列
        const exists = selectedColumns.some(col => col.prop === config.prop)
        if (!exists) {
          selectedColumns.push({ ...config })
          // 强制表格重新渲染，避免列错位闪烁
          tableKey.value++
        }
      }
    }

    // 选中的行数据
    const selectedRows = ref([])

    const handleSelectionChange = (selection) => {
      selectedCount.value = selection.length
      selectedRows.value = selection
    }

    const closeTab = (index) => {
      if (tabList.length > 1) {
        tabList.splice(index, 1)
        if (currentTab.value >= index) {
          currentTab.value = Math.max(0, currentTab.value - 1)
        }
      }
    }

    // 删除列
    const removeColumn = (index) => {
      selectedColumns.splice(index, 1)
      // 强制表格重新渲染
      tableKey.value++
    }

    // 切换收藏状态
    const toggleFavorite = async (col) => {
      if (!col.id) {
        ElMessage.warning('该指标不支持收藏')
        return
      }
      
      try {
        const response = await request.post(`/browser-config/favorite/${col.id}`)
        if (response.data && response.data.code === 200) {
          // 更新当前列的收藏状态
          col.favorites = response.data.favorites
          ElMessage.success(response.data.msg)
        } else {
          ElMessage.error(response.data.msg || '操作失败')
        }
      } catch (error) {
        console.error('收藏操作失败:', error)
        ElMessage.error('收藏操作失败')
      }
    }

    // 判断是否已收藏
    const isFavorite = (col) => {
      return col.favorites === 1
    }

    // 从后端加载表格数据
    const fetchData = async () => {
      loading.value = true
      try {
        const params = {
          pageNum: currentPage.value,
          pageSize: pageSize.value
        }
        // 如果有班级信息，添加班级筛选条件
        if (props.classInfo) {
          params.classCode = props.classInfo.classCode
        }
        // 如果有筛选条件，添加到参数
        if (currentFilters.value && currentFilters.value.length > 0) {
          params.filters = currentFilters.value
        }
        const response = await request.post('/student/page', params)
        const pageInfo = response.data
        // 清空当前数据并填充新数据
        tableData.splice(0, tableData.length)
        if (pageInfo.list && pageInfo.list.length > 0) {
          tableData.push(...pageInfo.list)
        }
        // 更新分页信息
        totalCount.value = pageInfo.total || 0
        filteredCount.value = pageInfo.total || 0

      } catch (error) {
        console.error('获取数据失败:', error)
        // 可以在这里添加错误提示
      } finally {
        loading.value = false
      }
    }

    // 从后端加载指标树数据
    const fetchTreeData = async () => {
      treeLoading.value = true
      try {
        // 构建请求参数
        const params = {}
        // 如果有班级信息，传入专业代码和学年用于过滤专业课
        if (props.classInfo) {
          params.majorCode = props.classInfo.majorCode
          params.academicYear = props.classInfo.academicYear
        }
        const response = await request.post('/browser-config/tree', params)
        // 清空当前数据并填充新数据
        indicatorTreeData.splice(0, indicatorTreeData.length)
        if (response.data && response.data.length > 0) {
          indicatorTreeData.push(...response.data)
        }
      } catch (error) {
        console.error('获取指标树数据失败:', error)
        // 可以在这里添加错误提示
      } finally {
        treeLoading.value = false
      }
    }

    // 从后端获取收藏的指标
    const fetchFavoriteIndicators = async () => {
      favoritesLoading.value = true;
      try {
        const response = await request.post('/browser-config/favorites');
        if (response.data && response.data.code === 200) {
          favoriteIndicators.splice(0, favoriteIndicators.length);
          if (response.data.data && response.data.data.length > 0) {
            favoriteIndicators.push(...response.data.data);
          }
        }
      } catch (error) {
        console.error('获取收藏指标失败:', error);
        ElMessage.error('获取收藏指标失败');
      } finally {
        favoritesLoading.value = false;
      }
    };

    // 处理收藏指标点击
    const handleFavoriteClick = (item) => {
      // 添加null检查
      if (!item) return;
      
      // 使用现有的 handleNodeClick 方法处理
      if (typeof handleNodeClick === 'function') {
        handleNodeClick(item);
      }
    };

    // 监听左侧标签页切换
    watch(currentLeftTab, (newTab) => {
      if (newTab === 1) {
        // 切换到我的指标时，获取收藏数据
        fetchFavoriteIndicators();
      }
    });

    // 监听分页参数变化，重新加载数据
    watch([currentPage, pageSize], () => {
      fetchData()
    })

    // 监听 classInfo 变化，当传入新的班级信息时重新查询学生和指标树
    watch(() => props.classInfo, (newClassInfo) => {
      if (newClassInfo) {
        currentPage.value = 1
        fetchData()
        fetchTreeData() // 重新加载指标树，根据新班级的专业和学年过滤专业课
      }
    }, { immediate: true })

    // 组件挂载时加载数据
    onMounted(() => {
      fetchData()
      fetchTreeData()
    })

    // 返回班级列表
    const handleBack = () => {
      router.push('/main')
    }

    // 清除班级筛选
    const handleClearFilter = () => {
      router.push('/main')
    }

    // 处理导出
    const handleExport = async (command) => {
      // 检查是否有班级信息
      if (!props.classInfo) {
        ElMessage.warning('请先选择班级')
        return
      }

      // 构建参数
      // 固定列：学号、姓名
      const fixedColumns = ['studentNo', 'name']
      // 动态列：用户选择的列
      const dynamicColumns = selectedColumns.map(col => col.prop)
      
      const params = {
        classCode: props.classInfo.classCode,
        exportType: command,
        columns: [...fixedColumns, ...dynamicColumns],  // 固定列 + 动态列
        filters: currentFilters.value  // 当前筛选条件
      }

      // 处理导出勾选
      if (command === 'selected') {
        // 获取表格选中的行（从 selection-change 事件保存的数据）
        if (selectedRows.value.length === 0) {
          ElMessage.warning('请先勾选要导出的学生')
          return
        }
        params.selectedStudentNos = selectedRows.value.map(row => row.studentNo)
      } else if (command === 'current') {
        // 导出当前页
        params.pageNum = currentPage.value
        params.pageSize = pageSize.value
      }
      // 'all' 不需要额外参数

      try {
        // 发送请求，设置 responseType: 'blob'
        const response = await request.post('/student/export', params, {
          responseType: 'blob'  // 关键：接收二进制数据
        })

        // 创建下载链接
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `学生数据_${new Date().getTime()}.xlsx`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        ElMessage.success('导出成功')
      } catch (error) {
        ElMessage.error('导出失败：' + error.message)
      }
    }

    // 处理筛选应用
    const handleFilterApply = (filters) => {
      currentFilters.value = filters
      // 重新加载数据，带上筛选条件
      fetchData()
    }

    // 构建筛选列（固定列 + 动态列）
    const filterColumns = computed(() => {
      const fixedColumns = [
        { prop: 'studentNo', label: '学号', indexType: 'STRING' },
        { prop: 'name', label: '姓名', indexType: 'STRING' }
      ]
      return [...fixedColumns, ...selectedColumns]
    })

    return {
      // 响应式数据
      currentLeftTab,
      selectedCount,
      currentPage,
      pageSize,
      totalCount,
      filteredCount,
      gotoPage,
      currentTab,
      tableKey,
      defaultProps,
      indicatorTreeData,
      treeLoading,
      favoriteIndicators,
      favoritesLoading,
      tableData,
      loading,
      tabList,
      selectedColumns,
      filterDialogVisible,
      filterColumns,
      indicatorManageDialogVisible,
      addIndicatorVisible,
      courseIndicatorList,
      editingIndicatorId,
      editingIndicatorName,
      addIndicatorForm,
      majorCode,
      academicYearSuffix,
      openIndicatorManage,
      handleEdit,
      handleSaveEdit,
      handleDelete,
      handleAddIndicator,
      User,
      studentManageDialogVisible,
      studentEditVisible,
      studentSearchKeyword,
      studentList,
      studentLoading,
      studentPageNum,
      studentPageSize,
      studentTotal,
      openStudentManage,
      handleDeleteStudent,
      onCollegeChange,
      onMajorChange,
      onCounselorChange,
      openAddStudent,
      openEditStudent,
      saveStudent,
      studentForm,
      studentRules,
      collegeList,
      majorList,
      classList,
      counselorList,
      areaOptions,
      User,
      
      // 方法
      handleNodeClick,
      handleSelectionChange,
      closeTab,
      removeColumn,
      toggleFavorite,
      isFavorite,
      handleBack,
      handleClearFilter,
      handleExport,
      handleFilterApply,
      handleSearchSelect,
      handleFavoriteClick,
      fetchFavoriteIndicators,
      
      // 图标变量，用于 el-button 的 :icon 属性
      Download,
      Filter,
      CircleClose,
      Management,
      Document
    }
  }
}
</script>

<style scoped src="./StockDataBrowser.css"></style>
